import { toAbsoluteUrl } from '@/lib/seo/canonical'

/**
 * WHICH PICTURE A SHARED LINK PREVIEWS, decided in exactly one place.
 *
 * Pure and browser-safe. Most of this platform's traffic arrives as a link
 * pasted into a Telegram chat, so the preview image is not decoration — it is
 * the first and often the only thing a visitor sees of a page before deciding
 * whether to open it.
 *
 * THE HIERARCHY, strongest first. `pickOgImage` takes candidates in this order
 * and returns the first one that is actually usable:
 *
 *   1. THE LISTING'S OWN PHOTO. A real photograph of the real thing beats any
 *      amount of branding, every time.
 *   2. THE MARKETPLACE'S OWN COVER OR LOGO. A community storefront previews as
 *      itself, not as the platform hosting it.
 *   3. A PAGE'S OWN ARTWORK, where one exists.
 *   4. THE BAZIO LOCKUP. The approved brand asset, and the answer for every
 *      page that has no content-specific image of its own.
 *
 * The fallback is never skipped. A page with no image at all previews as a bare
 * grey card with a hostname in it, which reads as a broken link rather than as
 * a marketplace.
 */

export type OgImage = {
    /** Absolute. A relative `og:image` is not resolved by most crawlers. */
    url: string
    width: number | null
    height: number | null
    alt: string
}

/** A candidate before it has been absolutised or checked. */
export type OgImageCandidate = {
    url: string | null | undefined
    alt: string
    width?: number | null
    height?: number | null
}

/**
 * THE APPROVED BAZIO SHARE IMAGE.
 *
 * 1200x630, the size every crawler renders `summary_large_image` at, and the
 * ratio the supplied artwork was already drawn to — it was resized from
 * 1731x909 and never stretched or recomposed.
 *
 * It ships in `public/` rather than on the CDN deliberately. The CDN host is
 * this project's own storage but is named after the founding COMMUNITY, so
 * serving the PLATFORM's brand asset from it put a tenant's name in the image
 * URL of every share preview on the site. The repo copy is the canonical one
 * and its absolute URL is built from the canonical origin.
 */
export const DEFAULT_OG_IMAGE_PATH = '/images/brand/bazio-og.png'

export const DEFAULT_OG_IMAGE_ALT = 'Bazio — Buy. Sell. Connect. Telegram communities turned into marketplaces.'

export const OG_WIDTH = 1200
export const OG_HEIGHT = 630

/** The brand fallback, absolutised against whichever origin is serving. */
export const defaultOgImage = (baseUrl?: string | null): OgImage => ({
    url: toAbsoluteUrl(baseUrl, DEFAULT_OG_IMAGE_PATH),
    width: OG_WIDTH,
    height: OG_HEIGHT,
    alt: DEFAULT_OG_IMAGE_ALT
})

/**
 * A URL is usable as a preview only if a crawler can fetch it without a
 * session. A `data:` or `blob:` URL cannot be fetched at all, and a relative
 * path is absolutised rather than rejected.
 */
const usable = (url: string | null | undefined): boolean => {
    const value = url?.trim()

    if (!value) return false
    if (/^(data|blob|javascript):/i.test(value)) return false

    return /^https?:\/\//i.test(value) || value.startsWith('/')
}

/**
 * The first candidate a crawler can actually use, or the brand lockup.
 *
 * Dimensions are only declared when they are KNOWN. Declaring 1200x630 for a
 * listing photo that is really 900x1600 makes Telegram reserve the wrong box
 * and renders the photo letterboxed or cropped against what was promised, so an
 * unknown size is left unstated and the crawler measures it itself.
 */
export const pickOgImage = (
    baseUrl: string | null | undefined,
    candidates: Array<OgImageCandidate | null | undefined>
): OgImage => {
    for (const candidate of candidates) {
        if (!candidate || !usable(candidate.url)) continue

        return {
            url: toAbsoluteUrl(baseUrl, candidate.url as string),
            width: candidate.width ?? null,
            height: candidate.height ?? null,
            alt: candidate.alt
        }
    }

    return defaultOgImage(baseUrl)
}

/**
 * The listing's own photo as a candidate.
 *
 * `media[0]` is the primary image — `createListing` writes `sort_order` in the
 * order the seller posted them and every read orders by it — and its stored
 * width and height travel with it, so the preview box is reserved correctly.
 * No collage is built from the rest: a crawler crops a collage unpredictably
 * and the result says less about the item than one clear photograph.
 */
export const listingOgImage = (listing: {
    title: string
    imageUrl?: string | null
    media?: Array<{ storageUrl: string, width?: number | null, height?: number | null }>
}): OgImageCandidate | null => {
    const primary = listing.media?.[0]
    const url = primary?.storageUrl ?? listing.imageUrl

    if (!usable(url)) return null

    return {
        url: url as string,
        width: primary?.width ?? null,
        height: primary?.height ?? null,
        alt: listing.title
    }
}

/**
 * A marketplace's own artwork as a candidate. The cover is drawn wide and is
 * the better preview; the logo is square and is the fallback.
 */
export const communityOgImage = (community: {
    name: string
    coverUrl?: string | null
    logoUrl?: string | null
}): OgImageCandidate | null => {
    const url = community.coverUrl ?? community.logoUrl

    if (!usable(url)) return null

    return { url: url as string, alt: `${community.name} on Bazio`, width: null, height: null }
}
